/* eslint-disable import/no-named-as-default-member */
import amplitude from 'amplitude-js';
import { debounce, DebouncedFunc } from 'lodash';
import ReactGA from 'react-ga4';

let amplitudeInitialized = false;

const GTM_POLL_INTERVAL_MS = 100;
const GTM_POLL_MAX_ATTEMPTS = 50; // 5 seconds max (50 * 100ms)

// Both our corporate website and the landing pages created on Unbounce (landingpage.outcomes4me.com)
// use Amplitude Browser SDK 2.0, which sets a cookie named AMP_{amplitude_key} scoped to .outcomes4me.com.
// The patient site uses amplitude-js v8, which uses a different cookie.
// To merge users across the two, we read the browser SDK cookie's device ID and session ID and pass them into the legacy SDK's init call.
function getBrowserSDKAmplitudeIds(): { deviceId: string | null; sessionId: number | null } {
  const empty = { deviceId: null, sessionId: null };
  try {
    const keyPrefix = (process.env.NEXT_PUBLIC_AMPLITUDE_KEY ?? '').substring(0, 10);
    if (!keyPrefix) return empty;
    const cookieName = `AMP_${keyPrefix}`;
    const cookie = document.cookie.split('; ').find((r) => r.startsWith(cookieName + '='));
    if (!cookie) return empty;
    const raw = cookie.split('=').slice(1).join('=');
    // Amplitude Browser SDK 2.0 cookie is encoded as: JSON → URL encode → base64
    // Therefore we decode as: base64 (atob) → URL decode → JSON.parse
    const decoded = decodeURIComponent(atob(raw));
    const parsed = JSON.parse(decoded) as { deviceId?: string; sessionId?: number };
    return {
      deviceId: parsed.deviceId || null,
      sessionId: parsed.sessionId || null,
    };
  } catch {
    return empty;
  }
}

const ANALYTICS_STORAGE = 'analytics_storage';

/**
 * Reads a GTM consent state without letting gtm.js internals throw at us.
 *
 * `google_tag_data.ics.getConsentState` is an undocumented GTM internal. It exists on the
 * page as soon as gtm.js runs, but it dereferences the per-type entry unconditionally, so
 * calling it before a consent default/update has fired for that type blows up inside gtm.js
 * with "undefined is not an object (evaluating 'b.usedContainerScopedDefaults')". That happens
 * whenever the container loads without Consent Mode having declared the type yet — a slow or
 * blocked CMP, a region where no default applies, or a partially blocked GTM.
 *
 * Returns undefined when consent for the type is simply unknown.
 */
function getGTMConsentState(consentType: string = ANALYTICS_STORAGE): number | undefined {
  if (typeof window === 'undefined') return undefined;
  const ics = window.google_tag_data?.ics;
  if (typeof ics?.getConsentState !== 'function' || ics.entries?.[consentType] === undefined) {
    return undefined;
  }
  try {
    return ics.getConsentState(consentType);
  } catch {
    // gtm.js changed shape on us — treat as unknown rather than surfacing their error.
    return undefined;
  }
}

export const initAmplitude = (
  userId?: number,
  { fetchConsentFromGTM }: { fetchConsentFromGTM?: boolean } = { fetchConsentFromGTM: false },
): void => {
  if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_AMPLITUDE_ENABLED === 'true') {
    if (process.env.NEXT_PUBLIC_AMPLITUDE_KEY) {
      const { deviceId: browserSDKDeviceId, sessionId: browserSDKSessionId } =
        getBrowserSDKAmplitudeIds();
      amplitude
        .getInstance()
        .init(process.env.NEXT_PUBLIC_AMPLITUDE_KEY || '', userId as string | undefined, {
          includeReferrer: true,
          includeFbclid: true,
          includeGclid: true,
          includeUtm: true,
          // Initialize by default. Only defer initialization if consent is explicitly denied. With GTM consent:
          // 1 == allowed, 2 == denied
          deferInitialization: fetchConsentFromGTM ? getGTMConsentState() === 2 : false,
          // If we found a cookie from the browser SDK, use its device ID and session ID to unify users across platforms. Otherwise, let amplitude-js generate new ones.
          ...(browserSDKDeviceId && { deviceId: browserSDKDeviceId }),
          ...(browserSDKSessionId !== null && { sessionId: browserSDKSessionId }),
        });
      amplitudeInitialized = true;
    } else {
      throw Error('Amplitude is enabled but NEXT_PUBLIC_AMPLITUDE_KEY is not defined');
    }
  }
};

/**
 * Enables Amplitude tracking after user consent is obtained.
 * Call this function when the user accepts analytics cookies via our consent management platform.
 */
export const enableAmplitudeTracking = (): void => {
  if (process.env.NEXT_PUBLIC_AMPLITUDE_ENABLED === 'true' && amplitudeInitialized) {
    amplitude.getInstance().setOptOut(false); // opt back in if previously opted out
    amplitude.getInstance().enableTracking();
  }
};

/**
 * Disables Amplitude tracking when user revokes consent.
 * Call this function when the user opts out of analytics cookies.
 */
export const setAmplitudeOptOut = (optOut: boolean): void => {
  if (process.env.NEXT_PUBLIC_AMPLITUDE_ENABLED === 'true' && amplitudeInitialized) {
    amplitude.getInstance().setOptOut(optOut);
  }
};

export function waitForGTM(thunk: () => void) {
  if (typeof window !== 'undefined') {
    let attempts = 0;
    const maxAttempts = GTM_POLL_MAX_ATTEMPTS;

    const checkGTM = setInterval(() => {
      attempts += 1;

      // Poll until GTM has actually recorded a consent state for analytics_storage. Checking
      // only that getConsentState exists is not enough: it is defined as soon as gtm.js loads,
      // but throws until a consent default/update has fired for the type.
      if (getGTMConsentState() !== undefined) {
        clearInterval(checkGTM);
        thunk();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkGTM);
        console.warn('GTM consent data not loaded after 5 seconds');
      }
    }, GTM_POLL_INTERVAL_MS);
  }
}

export function setAmplitudeConsentFromGTM() {
  const analyticsStorageConsent = getGTMConsentState();
  if (analyticsStorageConsent === 1) {
    enableAmplitudeTracking();
  } else if (analyticsStorageConsent === 2) {
    setAmplitudeOptOut(true);
  }
}

/**
 * @warning GA is not initalized in any Patient Web environments as of Sept 20 2022.
 * Google Analytics is only enabled in Trial Web environments.
 */
export const initGA = (): void => {
  // NEXT_PUBLIC_GA_ENABLED has been 'false' since Sept 2022 in all Patient Web environments
  if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_GA_ENABLED === 'true') {
    if (process.env.NEXT_PUBLIC_GA_ID) {
      ReactGA.initialize(process.env.NEXT_PUBLIC_GA_ID);
    } else {
      throw Error('Google Analytics is enabled but NEXT_PUBLIC_GA_ID is not defined');
    }
  }
};

export const initAnalytics = (
  userId?: number,
  { fetchConsentFromGTM }: { fetchConsentFromGTM?: boolean } = { fetchConsentFromGTM: false },
): void => {
  initAmplitude(userId, { fetchConsentFromGTM });
  if (typeof window !== 'undefined' && fetchConsentFromGTM) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => waitForGTM(setAmplitudeConsentFromGTM));
    } else {
      waitForGTM(setAmplitudeConsentFromGTM);
    }
  }
  initGA();
};

type GoogleEventData = {
  category?: string;
  label?: string;
  value?: number;
};

type LogEventData = GoogleEventData & Record<string, unknown>;

/**
 * Logs events to the following analytics services:
 * * Amplitude
 * * Google Analytics (except Patient Web as of Sept 2022)
 * * Google Tag Manager
 */
export const logEvent = (key: string, data?: LogEventData): void => {
  if (typeof window !== 'undefined') {
    if (process.env.NODE_ENV !== 'production') {
      console.log('logEvent', key, data);
    }
    // Deconstruct the 3 props that is used for GA and GTM.
    // For Amplitude can be any value in the data structure.
    const { category = '', label, value } = (data as GoogleEventData) || {};

    if (process.env.NEXT_PUBLIC_AMPLITUDE_ENABLED === 'true' && amplitudeInitialized) {
      amplitude.getInstance().logEvent(key, data);
    }
    // NEXT_PUBLIC_GA_ENABLED has been 'false' since Sept 2022 in all Patient Web environments
    // Only true for Trial Web environments
    if (process.env.NEXT_PUBLIC_GA_ENABLED === 'true') {
      ReactGA.event({
        action: key,
        category,
        label,
        value,
      });
    }
    if (process.env.NEXT_PUBLIC_GTM_ID && process.env.NEXT_PUBLIC_GTM_ID !== 'DUMMY') {
      window.dataLayer.push({
        event: key,
        category,
        label,
        value,
      });
    }
  }
};

// Dumb hack to prevent us firing multiple duplicate events from certain useEffect hooks
const debouncers: Record<string, DebouncedFunc<(key: string, data?: LogEventData) => void>> = {};
export const debouncedLogEvent = (key: string, data?: LogEventData): void => {
  if (!debouncers[key]) {
    debouncers[key] = debounce(logEvent, 1000, {
      leading: true,
      trailing: false,
    });
  }
  debouncers[key](key, data);
};

export const pageView = (url: string): void => {
  if (typeof window !== 'undefined') {
    if (process.env.NODE_ENV !== 'production') {
      console.log('pageView', url);
    }
    if (process.env.NEXT_PUBLIC_AMPLITUDE_ENABLED === 'true' && amplitudeInitialized) {
      amplitude.getInstance().logEvent('page_view', { url });
    }
    // NEXT_PUBLIC_GA_ENABLED has been 'false' since Sept 2022 in all Patient Web enviornments
    // Only true for Trial Web enviornments
    if (process.env.NEXT_PUBLIC_GA_ENABLED === 'true') {
      ReactGA.send({ hitType: 'pageview', page: url });
    }
  }
};

export const pageViewTagManager = (url: string): void => {
  window.dataLayer.push({
    event: 'pageview',
    page: url,
  });
};

const TEST_USER_STORAGE_KEY = 'amplitude_is_test_user';

/**
 * Reads the isTestUser flag from localStorage and re-stamps it as an Amplitude user property
 * on the current identity. Called whenever the Amplitude user identity changes (e.g. after
 * setUserId) so the flag is never lost when a new patient ID is assigned.
 */
function reapplyTestUserFlag(): void {
  if (typeof window === 'undefined') return;
  if (process.env.NEXT_PUBLIC_AMPLITUDE_ENABLED !== 'true' || !amplitudeInitialized) return;
  const isTestUser = localStorage.getItem(TEST_USER_STORAGE_KEY) === 'true';
  amplitude.getInstance().setUserProperties({ isTestUser });
}

export const setUserId = (id: number | null): void => {
  if (process.env.NEXT_PUBLIC_AMPLITUDE_ENABLED === 'true' && amplitudeInitialized) {
    amplitude.getInstance().setUserId(id as string | null);
    // Re-stamp isTestUser on the new identity so it isn't lost when a new patient ID is assigned
    // (e.g. after a SCRI session expiry where the user re-authenticates with a fresh user ID).
    reapplyTestUserFlag();
  }
};

export const getUserId = (): string | undefined => {
  if (process.env.NEXT_PUBLIC_AMPLITUDE_ENABLED === 'true' && amplitudeInitialized) {
    return amplitude.getInstance().getUserId();
  }
  return undefined;
};

export const reset = (): void => {
  if (process.env.NEXT_PUBLIC_AMPLITUDE_ENABLED === 'true' && amplitudeInitialized) {
    // See: https://amplitude.com/docs/sdks/analytics/browser/javascript-sdk#logged-out-and-anonymous-users
    amplitude.getInstance().setUserId(null); // not string 'null'
    amplitude.getInstance().regenerateDeviceId();
  }
};

export const setUserProperties = (properties: Record<string, unknown>): void => {
  if (process.env.NEXT_PUBLIC_AMPLITUDE_ENABLED === 'true' && amplitudeInitialized) {
    amplitude.getInstance().setUserProperties(properties);
  }
};

export const initOrUpdateAmplitude = (userId: number | null): void => {
  if (amplitudeInitialized && amplitude.getInstance().isNewSession()) {
    setUserId(userId);
  } else {
    initAmplitude(userId || undefined);
  }
};

export const initOrUpdateAnalytics = (userId: number | null): void => {
  if (amplitudeInitialized && amplitude.getInstance().isNewSession()) {
    setUserId(userId);
  } else {
    initAnalytics(userId || undefined);
  }
};

/**
 * Allows users to flag themselves as test users in Amplitude by visiting any page
 * with `?isTestUser=true` in the URL. The flag is persisted in localStorage so it survives
 * page reloads and new sessions without needing to re-add the URL param.
 *
 * To clear the flag: visit any page with `?isTestUser=false`.
 *
 * Call this once at app startup, immediately after initAmplitude / initAnalytics.
 */
export const syncTestUserFlag = (): void => {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);

  if (params.has('isTestUser')) {
    const isTestUser = params.get('isTestUser') === 'true';

    if (isTestUser) {
      localStorage.setItem(TEST_USER_STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(TEST_USER_STORAGE_KEY);
    }

    // Strip the param from the visible URL without triggering a reload
    params.delete('isTestUser');
    const newUrl = [
      window.location.pathname,
      params.toString() ? `?${params.toString()}` : '',
      window.location.hash,
    ].join('');
    window.history.replaceState({}, '', newUrl);
  }

  // Re-apply on every app init so the property is set even if Amplitude reinitialized
  reapplyTestUserFlag();
};
